//
// Roles and capabilities — pure logic, usable on both client and server.
//
// This module deliberately has NO imports: no i18next, no client store, no
// @shared/index.tsx (which pulls in prettier and JSX). Server code must import
// from here, not from './roles', so it does not drag the client bundle along.
//
// './roles' re-exports everything below and adds the client-only helpers that
// fall back to the logged-in user.
//

export enum RoleType {
    Administrator = 'administrator', // the admin
    Owner = 'owner', // the subscription owner, can do everything in the tenant
    ProjectLeader = 'projectleader', // the project leader
    ProjectMember = 'projectmember', // a meber of the project team
    SystemDeveloper = 'systemdeveloper', // a system developer
    ModuleDeveloper = 'moduledeveloper', // a module developer
    Viewer = 'viewer', // can only read some project parts
}

export type TRole = { role: RoleType; caps: string[]; canAssignRoles: RoleType[] }

export enum Capabilities {
    tenant_read = 'tenant_read',
    tenant_write = 'tenant_write',

    owner_read = 'owner_read',
    owner_write = 'owner_write',

    data_list_read = 'data_list_read',
    data_list_write = 'data_list_write',
    data_list_delete = 'data_list_delete',

    export_read = 'export_read',
    export_write = 'export_write',
    export_delete = 'export_delete',

    convert_read = 'convert_read',
    convert_write = 'convert_write',
    convert_delete = 'convert_delete',
    // Writing converter SOURCE CODE, which is a different thing from assigning a
    // column: module code is imported into the server process and runs there
    // unsandboxed (server/utils/dynamicModule.ts). convert_write cannot express
    // that difference — every role except Viewer holds it — so it gets its own.
    // A fresh value, not an alias: see the note on user_read_assigned_project.
    converter_code_write = 'converter_code_write',

    template_read = 'template_read',
    template_write = 'template_write',
    template_delete = 'template_delete',
    template_pause = 'template_pause',
    template_archive = 'template_archive',

    user_read = 'user_read',
    user_write = 'user_write',
    user_delete = 'user_delete',
    // NOTE: these three intentionally carry the same values as user_read/_write/_delete.
    // Persist capability VALUES, never enum names, or you get silent duplicates.
    user_read_assigned_project = 'user_read',
    user_write_assign_project = 'user_write',
    user_delete_assign_project = 'user_delete',

    project_read = 'project_read',
    project_write = 'project_write',
    project_delete = 'project_delete',
    project_read_assigned_user = 'project_read_assigned_user',
    project_write_assign_user = 'project_write_assigned_user',
    project_delete_assign_user = 'project_delete_assigned_user',

    settings_read = 'settings_read',
    settings_write = 'settings_write',

    subscription_read = 'subscription_read',
    subscription_write = 'subscription_write',
    subscription_delete = 'subscription_delete',
    subscription_profile = 'subscription_profile',
}

export const capabilities = Object.values(Capabilities).filter((v) => typeof v === 'string') as string[]

export const roles: TRole[] = [
    {
        role: RoleType.Administrator,
        caps: capabilities,
        canAssignRoles: Object.values(RoleType),
    },
    {
        role: RoleType.Owner,
        caps: [
            Capabilities.project_read, Capabilities.project_write,
            Capabilities.project_read_assigned_user,
            Capabilities.project_write_assign_user,
            Capabilities.user_read, Capabilities.user_write,
            Capabilities.user_read_assigned_project,
            Capabilities.user_write_assign_project,
            Capabilities.template_read, Capabilities.template_write, Capabilities.template_pause, Capabilities.template_archive,
            Capabilities.data_list_read, Capabilities.data_list_write,
            Capabilities.convert_read, Capabilities.convert_write,
            Capabilities.converter_code_write,
            Capabilities.export_read, Capabilities.export_write,
            Capabilities.subscription_profile,
            // The tenant's own settings. Only administrators held this, which
            // left an owner unable to configure their own tenant.
            Capabilities.settings_write,
        ],
        canAssignRoles: [RoleType.ProjectLeader, RoleType.ProjectMember, RoleType.Viewer],
    },
    {
        role: RoleType.ProjectLeader,
        caps: [
            Capabilities.project_read, Capabilities.project_write,
            Capabilities.project_read_assigned_user, Capabilities.project_write_assign_user,
            Capabilities.user_read, Capabilities.user_write,
            Capabilities.user_read_assigned_project, Capabilities.user_write_assign_project,
            Capabilities.template_read, Capabilities.template_write, Capabilities.template_pause, Capabilities.template_archive,
            Capabilities.data_list_read, Capabilities.data_list_write,
            Capabilities.convert_read, Capabilities.convert_write,
            Capabilities.converter_code_write,
            Capabilities.export_read, Capabilities.export_write,
        ],
        canAssignRoles: [RoleType.ProjectMember, RoleType.Viewer],
    },
    {
        role: RoleType.ProjectMember,
        caps: [
            Capabilities.template_read, Capabilities.template_write,
            Capabilities.project_read,
            Capabilities.data_list_read,
            Capabilities.convert_read, Capabilities.convert_write,
            // Deliberately NOT converter_code_write (operator's decision,
            // 2026-09-05): a project member assigns columns, and the code editor
            // is closed to them. They keep convert_write and lose nothing else.
            Capabilities.export_read, Capabilities.export_write,
        ],
        canAssignRoles: [],
    },
    {
        role: RoleType.SystemDeveloper,
        caps: [
            Capabilities.template_read, Capabilities.template_write,
            Capabilities.data_list_read, Capabilities.data_list_write,
            Capabilities.convert_read, Capabilities.convert_write,
            Capabilities.converter_code_write,
            Capabilities.export_read, Capabilities.export_write,
        ],
        canAssignRoles: [],
    },
    {
        role: RoleType.ModuleDeveloper,
        caps: [
            Capabilities.template_read, Capabilities.template_write,
            //Capabilities.data_list_read,
            Capabilities.convert_read, Capabilities.convert_write,
            Capabilities.converter_code_write,
            Capabilities.export_read, Capabilities.export_write,
        ],
        canAssignRoles: [],
    },
    {
        role: RoleType.Viewer,
        caps: [
            Capabilities.project_read,
            Capabilities.data_list_read,
            Capabilities.convert_read,
            Capabilities.export_read,
        ],
        canAssignRoles: [],
    },
]

/**
 * Returns the capabilities for the given role
 * @param role
 * @returns
 */
export const getCapabilities = (role: RoleType) => {
    const r = roles.find((item) => item.role === role)
    if (!r) return []
    return r.caps
}

/**
 * Returns true if the role contains the capability cap.
 * Pure: the role is mandatory, there is no implicit "current user" fallback.
 * @param cap
 * @param role
 * @returns
 */
export const hasPermissionFor = (cap: Capabilities, role: RoleType | null | undefined) => {
    if (!cap) return false
    if (!role) return false
    if (capabilities.indexOf(cap) === -1) return false

    const caps = getCapabilities(role)
    if (!caps || caps.indexOf(cap) === -1) return false

    return true
}

/**
 * Returns true if the given role is in the specified roles.
 * Pure: the role is mandatory, there is no implicit "current user" fallback.
 * @param roles
 * @param userRole
 * @returns
 */
export const hasRoleOf = (allowed: RoleType | RoleType[], userRole: RoleType | null | undefined) => {
    if (!allowed) return false
    if (!Array.isArray(allowed)) allowed = [allowed]

    if (!userRole) return false
    return allowed.indexOf(userRole) !== -1
}

/**
 * Returns true if the actorRole can assign the targetRole
 * @param actorRole
 * @param targetRole
 * @returns
 */
export function canAssignRole(actorRole: RoleType, targetRole: RoleType): boolean {
    const roleConfig = roles.find((item) => item.role === actorRole)
    return !!roleConfig?.canAssignRoles.includes(targetRole)
}

//
// ==========================================================================
// Who may write converter source code, and under which rules.
// ==========================================================================
//
// Two separate questions, deliberately kept apart:
//
//   1. May this role write code at all?  → Capabilities.converter_code_write
//   2. May it also import packages?      → codeImportRoles below
//
// The rules themselves live in server/utils/codePolicy.ts. Everything about WHO
// is in the two data structures here, so a change of mind about the line-up is
// an edited line, not a rebuild.
//

export type CodeClass =
    /** Imports allowed, checked against the packages modules/global.ts promises. */
    | 'free'
    /** May write code, but no imports, no require/import(), no eval, no process. */
    | 'restricted'
    /** May not write code at all. */
    | 'none'

/**
 * The free class. Settled: administrators and the two developer roles.
 *
 * ⚠️ Whoever is NOT listed here but holds converter_code_write is restricted.
 * The Viewer holds neither and cannot write code by any route.
 */
export const codeImportRoles: RoleType[] = [
    RoleType.Administrator,
    RoleType.SystemDeveloper,
    RoleType.ModuleDeveloper,
]

/**
 * Which rules apply to code saved by this role.
 */
export const codeClassFor = (role: RoleType | null | undefined): CodeClass => {
    if (!role) return 'none'
    if (!hasPermissionFor(Capabilities.converter_code_write, role)) return 'none'
    return codeImportRoles.includes(role) ? 'free' : 'restricted'
}
